import React, { useState } from 'react';
import { DataProviderInstance } from '../../api/DataProvider';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import {
    Alert,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Tooltip
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ModulePreviewPopup from './ModulePreviewPopup';
import {
    APPOINTMENT_MODULES,
    MODULE_CATEGORIES,
    MODULE_KEYS,
    getCategoryLabel,
    getModuleByKey
} from '../../pages/appointment/modules/appointmentModules';
import styles from './CreateEditAppointmentType.module.css';

const ASK_TEMPLATE = '';
const ALL_CATEGORIES = 'all';

/**
 * Criação/edição de um tipo de atendimento. À esquerda, os módulos disponíveis,
 * filtráveis por categoria (um clique para adicionar); à direita, os que entram
 * no atendimento, na ordem em que os cards aparecem (setas para reordenar, X
 * para tirar).
 * Abaixo, o modelo de anamnese fixo (quando o módulo Anamnese está ligado), as
 * medidas pedidas pelo módulo Medidas corporais (quando ligado) e a marcação
 * de tipo padrão.
 */
const CreateEditAppointmentType = ({ Data, OnUpdate, OnHide }) => {
    const { CompanyConfig, SetAppointmentType, UpdateAppointmentType } = DataProviderInstance();
    const anamnesisTemplates = CompanyConfig?.AnamneseTemplates || [];

    const [label, setLabel] = useState(Data ? Data.Label : '');
    const [modules, setModules] = useState(Data ? [...(Data.Modules || [])] : []);
    const [anamnesisTemplateId, setAnamnesisTemplateId] = useState(
        Data?.Config?.anamnesis?.templateId ? String(Data.Config.anamnesis.templateId) : ASK_TEMPLATE
    );
    const [isDefault, setIsDefault] = useState(Data ? !!Data.IsDefault : false);
    const [category, setCategory] = useState(ALL_CATEGORIES);
    const [previewModule, setPreviewModule] = useState(null);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    // O padrão só deixa de ser padrão quando outro tipo é marcado
    const lockedDefault = !!Data?.IsDefault;
    const hasAnamnesis = modules.includes(MODULE_KEYS.ANAMNESIS);
    const validTemplateId = anamnesisTemplates.some((template) => String(template.Id) === anamnesisTemplateId)
        ? anamnesisTemplateId
        : ASK_TEMPLATE;

    const selectedModules = modules.map(getModuleByKey).filter(Boolean);
    const unselectedModules = APPOINTMENT_MODULES.filter((module) => !modules.includes(module.key));
    const availableModules = unselectedModules.filter(
        (module) => category === ALL_CATEGORIES || module.category === category
    );

    const handleAddModule = (key) => setModules([...modules, key]);
    const handleRemoveModule = (key) => setModules(modules.filter((item) => item !== key));

    const handleMoveModule = (index, direction) => {
        const target = direction === 'up' ? index - 1 : index + 1;
        if (target < 0 || target >= modules.length) return;
        const next = [...modules];
        [next[index], next[target]] = [next[target], next[index]];
        setModules(next);
    };

    const handleConfirm = () => {
        if (!label.trim()) {
            setError('Informe o nome do tipo de atendimento.');
            return;
        }
        if (modules.length === 0) {
            setError('Adicione pelo menos um módulo.');
            return;
        }

        const config = { ...(Data?.Config || {}) };
        if (hasAnamnesis && validTemplateId !== ASK_TEMPLATE) {
            config.anamnesis = { templateId: Number(validTemplateId) };
        } else {
            delete config.anamnesis;
        }

        const payload = {
            label: label.trim(),
            modules,
            config: Object.keys(config).length > 0 ? config : null,
            isDefault
        };

        setError('');
        setSaving(true);
        const done = (response) => {
            setSaving(false);
            if (response?.status === 'SUCCESS') {
                OnUpdate();
            } else {
                setError(response?.error || 'Não foi possível salvar o tipo de atendimento.');
            }
        };

        if (Data) {
            UpdateAppointmentType({ id: Data.Id, ...payload }, done);
        } else {
            SetAppointmentType(payload, done);
        }
    };

    const renderPreviewButton = (module) => (
        <Tooltip title="Ver prévia do card">
            <IconButton
                size="small"
                aria-label={`Ver prévia de ${module.label}`}
                onClick={(e) => {
                    e.stopPropagation();
                    setPreviewModule(module);
                }}
            >
                <VisibilityOutlinedIcon fontSize="inherit" />
            </IconButton>
        </Tooltip>
    );

    const renderSelectedModule = (module, index) => (
        <div key={module.key} className={`${styles.moduleItem} ${styles.moduleItemSelected}`}>
            <span className={styles.moduleIndex}>{index + 1}</span>
            <div className={styles.moduleText}>
                <span className={styles.moduleLabel}>{module.label}</span>
                <span className={styles.moduleDescription}>{module.description}</span>
            </div>
            <div className={styles.moduleActions}>
                {renderPreviewButton(module)}
                <IconButton size="small" aria-label={`Subir ${module.label}`} onClick={() => handleMoveModule(index, 'up')} disabled={index === 0}>
                    <ArrowUpwardIcon fontSize="inherit" />
                </IconButton>
                <IconButton size="small" aria-label={`Descer ${module.label}`} onClick={() => handleMoveModule(index, 'down')} disabled={index === modules.length - 1}>
                    <ArrowDownwardIcon fontSize="inherit" />
                </IconButton>
                <Tooltip title="Tirar do atendimento">
                    <IconButton size="small" aria-label={`Tirar ${module.label} do atendimento`} onClick={() => handleRemoveModule(module.key)}>
                        <CloseIcon fontSize="inherit" />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    );

    const renderAvailableModule = (module) => (
        <div
            key={module.key}
            className={`${styles.moduleItem} ${styles.moduleItemAvailable}`}
            onClick={() => handleAddModule(module.key)}
            role="button"
        >
            <div className={styles.moduleText}>
                <span className={styles.moduleLabel}>
                    {module.label}
                    <span className={styles.moduleCategory}>{getCategoryLabel(module.category)}</span>
                </span>
                <span className={styles.moduleDescription}>{module.description}</span>
            </div>
            <div className={styles.moduleActions}>
                {renderPreviewButton(module)}
                <IconButton size="small" color="primary" aria-label={`Adicionar ${module.label}`}>
                    <AddIcon fontSize="inherit" />
                </IconButton>
            </div>
        </div>
    );

    const getContent = () => (
        <div className={styles.container}>
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

            <TextField
                label="Nome do tipo de atendimento"
                variant="outlined"
                fullWidth
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                inputProps={{ maxLength: 100 }}
                sx={{ mt: 1 }}
            />

            <div className={styles.columns}>
                <div className={styles.column}>
                    <span className={styles.columnTitle}>Disponíveis ({unselectedModules.length})</span>
                    <span className={styles.hint}>Clique em um módulo para adicioná-lo. O olho mostra uma prévia do card.</span>
                    <div className={styles.filterBar}>
                        {[{ key: ALL_CATEGORIES, label: 'Todos' }, ...MODULE_CATEGORIES].map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                className={`${styles.filterBtn} ${category === item.key ? styles.filterBtnActive : ''}`}
                                onClick={() => setCategory(item.key)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.moduleList}>
                        {availableModules.length === 0 ? (
                            <div className={styles.emptyColumn}>
                                {unselectedModules.length === 0
                                    ? 'Todos os módulos já estão no atendimento.'
                                    : 'Nenhum módulo desta categoria fora do atendimento.'}
                            </div>
                        ) : (
                            availableModules.map(renderAvailableModule)
                        )}
                    </div>
                </div>

                <div className={styles.column}>
                    <span className={styles.columnTitle}>No atendimento ({selectedModules.length})</span>
                    <span className={styles.hint}>Os cards aparecem nesta ordem na tela de atendimento.</span>
                    <div className={styles.moduleList}>
                        {selectedModules.length === 0 ? (
                            <div className={styles.emptyColumn}>Adicione módulos da lista ao lado.</div>
                        ) : (
                            selectedModules.map(renderSelectedModule)
                        )}
                    </div>
                </div>
            </div>

            {hasAnamnesis && (
                <div className={styles.section}>
                    <span className={styles.columnTitle}>Anamnese</span>
                    {anamnesisTemplates.length === 0 ? (
                        <span className={styles.hint}>
                            Nenhum modelo de anamnese cadastrado. Crie um na aba Anamneses para fixá-lo neste tipo.
                        </span>
                    ) : (
                        <>
                            <FormControl fullWidth size="small">
                                <InputLabel id="appointment-type-anamnesis-label">Modelo de anamnese</InputLabel>
                                <Select
                                    labelId="appointment-type-anamnesis-label"
                                    label="Modelo de anamnese"
                                    value={validTemplateId}
                                    onChange={(e) => setAnamnesisTemplateId(e.target.value)}
                                >
                                    <MenuItem value={ASK_TEMPLATE}>Escolher na hora do atendimento</MenuItem>
                                    {anamnesisTemplates.map((template) => (
                                        <MenuItem key={template.Id} value={String(template.Id)}>
                                            {template.Label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <span className={styles.hint}>
                                Com um modelo fixo, o atendimento já abre o botão para preencher esse modelo.
                            </span>
                        </>
                    )}
                </div>
            )}

            <div className={styles.section}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={isDefault}
                            onChange={(e) => setIsDefault(e.target.checked)}
                            disabled={lockedDefault}
                        />
                    }
                    label="Tipo padrão (sugerido ao iniciar um atendimento)"
                />
                {lockedDefault && (
                    <span className={styles.hint}>
                        Este é o tipo padrão da clínica. Para trocar, marque outro tipo como padrão.
                    </span>
                )}
            </div>
        </div>
    );

    const getFooter = () => (
        <div className={styles.footer}>
            <CustomButton
                variant="outlined"
                style="secondary"
                label="Cancelar"
                onClick={OnHide}
            />
            <CustomButton
                variant="contained"
                style="primary"
                label={Data ? 'Salvar alterações' : 'Criar tipo'}
                onClick={handleConfirm}
                enabled={!saving}
                loading={saving}
            />
        </div>
    );

    return (
        <>
            <PopupBase
                Title={Data ? 'Editar tipo de atendimento' : 'Novo tipo de atendimento'}
                Content={getContent()}
                Footer={getFooter()}
                OnClose={OnHide}
                maxWidth="md"
            />
            {previewModule && (
                <ModulePreviewPopup
                    module={previewModule}
                    onAdd={modules.includes(previewModule.key) ? null : handleAddModule}
                    onClose={() => setPreviewModule(null)}
                />
            )}
        </>
    );
};

export default CreateEditAppointmentType;
